/**
 * Page Tableau de charge — RH / Atelier
 * Structure : 4 lignes par technicien (Contrat / Affectées / Pointées / Écart)
 * Colonnes : Lun–Dim + TOTAL  (BASE supprimée)
 * Clic sur cellule → panneau de détail journalier
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, RefreshCw, X, Clock, ExternalLink, Plus } from 'lucide-react';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getLundi(iso) {
  const [y, m, day] = iso.split('-').map(Number);
  const d = new Date(y, m - 1, day); // minuit LOCAL — évite le décalage UTC
  const diff = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - diff);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayISO() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

function addDays(iso, n) {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtH(h, fallback = '—') {
  if (h === null || h === undefined || h === 0) return fallback;
  const hh = Math.floor(h);
  const mm  = Math.round((h - hh) * 60);
  return mm > 0 ? `${hh}h${String(mm).padStart(2, '0')}` : `${hh}h`;
}

function fmtWeek(lundi) {
  const dimanche = addDays(lundi, 6);
  const d1 = new Date(lundi    + 'T12:00:00');
  const d2 = new Date(dimanche + 'T12:00:00');
  const mois1 = d1.toLocaleDateString('fr-FR', { month: 'short' });
  const mois2 = d2.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
  return `${d1.getDate()} ${mois1 === mois2.split(' ')[0] ? '' : mois1} – ${d2.getDate()} ${mois2}`;
}

function fmtDateLong(iso) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

const JOURS_COURTS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// ─── Écart : valeur et style (Pointées − Contrat) ───────────────────────────

function ecartInfo(contrat, pointees) {
  // Pas de contrat (ex : week-end ou non renseigné) → tiret discret
  if (!contrat || contrat === 0) return { label: '—', color: 'var(--text-disabled)', weight: 400 };
  // Pointées non encore saisies
  if (!pointees || pointees === 0) return { label: '—', color: 'var(--text-disabled)', weight: 400 };
  const ecart = pointees - contrat;
  if (Math.abs(ecart) < 0.1) return { label: '=', color: '#15803D', weight: 700 };
  if (ecart > 0) return { label: `+${fmtH(ecart)}`, color: '#1D4ED8', weight: 700 };
  return { label: `−${fmtH(Math.abs(ecart))}`, color: '#DC2626', weight: 700 };
}

// ─── Panneau de détail journalier ─────────────────────────────────────────────

const inp   = "w-full px-3 py-2 rounded-lg border text-sm focus:outline-none";
const inpSt = { borderColor: 'var(--border)', background: 'white' };

function DetailPanel({ tech, iso, onClose, onRefresh }) {
  const navigate = useNavigate();
  const [formMode, setFormMode]   = useState(null); // null | 'or' | 'tnf'
  const [saving,   setSaving]     = useState(false);

  // Formulaire OR
  const [orSearch,   setOrSearch]   = useState('');
  const [orOptions,  setOrOptions]  = useState([]);
  const [orLoading,  setOrLoading]  = useState(false);
  const [orSelected, setOrSelected] = useState(null);
  const [orDuree,    setOrDuree]    = useState('');
  const [orNote,     setOrNote]     = useState('');

  // Formulaire TNF
  const [typesTNF,      setTypesTNF]      = useState([]);
  const [tnfTypeId,     setTnfTypeId]     = useState('');
  const [tnfDateDebut,  setTnfDateDebut]  = useState(iso);
  const [tnfDateFin,    setTnfDateFin]    = useState(iso);
  const [tnfDuree,      setTnfDuree]      = useState('');
  const [tnfNote,       setTnfNote]       = useState('');

  // Charger types TNF au premier clic
  useEffect(() => {
    if (formMode === 'tnf' && typesTNF.length === 0) {
      api.getTypesTNF()
        .then(r => setTypesTNF((Array.isArray(r.data) ? r.data : []).filter(t => t.actif !== false)))
        .catch(() => {});
    }
  }, [formMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autocomplete OR
  useEffect(() => {
    if (orSearch.length < 2) { setOrOptions([]); return; }
    const t = setTimeout(() => {
      setOrLoading(true);
      api.getOrdresReparation({ search: orSearch, limit: 10, statut: 'en_cours' })
        .then(r => setOrOptions(r.data?.items || r.data || []))
        .catch(() => {})
        .finally(() => setOrLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [orSearch]);

  const toggleForm = (mode) => {
    setFormMode(prev => prev === mode ? null : mode);
    setOrSearch(''); setOrSelected(null); setOrDuree(''); setOrNote('');
    setTnfTypeId(''); setTnfDateDebut(iso); setTnfDateFin(iso); setTnfDuree(''); setTnfNote('');
  };

  const handleAddOR = async () => {
    if (!orSelected) { toast.error('Sélectionnez un OR'); return; }
    const d = parseFloat(orDuree);
    if (!orDuree || isNaN(d) || d <= 0) { toast.error('Durée invalide'); return; }
    setSaving(true);
    try {
      await api.createORTechnicien({
        ordre_id: orSelected.ordre_id,
        employe_id: tech.employe_id,
        date_affectation: iso,
        duree_affectee: d,
        note: orNote,
      });
      toast.success('OR affecté');
      toggleForm(null);
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleAddTNF = async () => {
    if (!tnfTypeId) { toast.error('Sélectionnez un type TNF'); return; }
    const d = parseFloat(tnfDuree);
    if (!tnfDuree || isNaN(d) || d <= 0) { toast.error('Durée invalide'); return; }
    if (tnfDateFin < tnfDateDebut) { toast.error('Date de fin < date de début'); return; }
    setSaving(true);
    try {
      const nb = (() => {
        const d1 = new Date(tnfDateDebut); const d2 = new Date(tnfDateFin);
        return Math.round((d2 - d1) / 86400000) + 1;
      })();
      await api.createTNF({
        employe_id:     tech.employe_id,
        type_tnf_id:    tnfTypeId,
        date:           tnfDateDebut,
        date_fin:       tnfDateFin,
        duree_reelle: d,
        note:           tnfNote,
      });
      toast.success(nb > 1 ? `${nb} TNF créés (${tnfDateDebut} → ${tnfDateFin})` : 'TNF ajouté');
      toggleForm(null);
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const jour = tech.jours[iso] || { detail_ort: [], detail_tnf: [] };
  const ort  = jour.detail_ort || [];
  const tnf  = jour.detail_tnf || [];
  const all  = [
    ...ort.map(o => ({
      type:     'OR',
      ref:      o.or_numero,
      client:   o.or_client_nom || '—',
      materiel: [o.mat_marque, o.mat_modele].filter(Boolean).join(' ') || '—',
      serie:    o.mat_serie ? `N° ${o.mat_serie}` : '—',
      titre:    o.or_titre || '—',
      affecte:  o.duree_affectee || 0,
      pointe:   o.duree_reelle,
      navTo:    () => navigate(`/atelier?open_or=${o.ordre_id}`),
    })),
    ...tnf.map(t => ({
      type:     'TNF',
      ref:      t.type_tnf_libelle || '—',
      client:   '—',
      materiel: '—',
      serie:    '—',
      titre:    t.note || '—',
      affecte:  0,                        // TNF non comptées dans affectées
      pointe:   t.duree_reelle,         // pointées via duree_reelle
      navTo:    () => navigate('/rh/tnf'),
    })),
  ];
  const totalAff = all.reduce((s, r) => s + r.affecte, 0);
  const totalPt  = all.filter(r => r.pointe != null).reduce((s, r) => s + (r.pointe || 0), 0);

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)', marginTop: '3rem' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4"
        style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
        <h2 className="text-xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
          Détail de la journée
          <span className="ml-3 text-base font-bold" style={{ color: 'var(--text)', fontFamily: 'inherit' }}>
            {tech.nom}
          </span>
          <span className="ml-2 text-sm font-normal" style={{ color: 'var(--text-secondary)', fontFamily: 'inherit' }}>
            · {fmtDateLong(iso)}
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => toggleForm('or')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity"
            style={{ background: 'var(--primary-gradient)', opacity: formMode === 'tnf' ? 0.55 : 1 }}>
            <Plus size={14} />
            Affecter un OR
          </button>
          <button onClick={() => toggleForm('tnf')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity"
            style={{ background: 'var(--primary-gradient)', opacity: formMode === 'or' ? 0.55 : 1 }}>
            <Plus size={14} />
            Créer un TNF
          </button>
          <button onClick={onClose} className="ml-1 p-1.5 rounded hover:bg-gray-100"
            style={{ color: 'var(--text-muted)' }}>
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Table */}
      {all.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-light)' }}>
                {[
                  { label: 'Type',       w: 70  },
                  { label: 'Référence',  w: 120 },
                  { label: 'Client',     w: 100 },
                  { label: 'Matériel',   w: 130 },
                  { label: 'N° Série',   w: 90  },
                  { label: 'Titre OR',   w: 220 },
                  { label: 'Affecté',    w: 75, center: true },
                  { label: 'Pointé',     w: 75, center: true },
                ].map(({ label, w, center }) => (
                  <th key={label}
                    className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${center ? 'text-center' : 'text-left'}`}
                    style={{ color: 'var(--text-muted)', width: w || undefined, minWidth: w || undefined }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {all.map((row, i) => (
                <tr key={i}
                  onClick={row.navTo}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  style={{ borderBottom: i < all.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold px-1.5 py-0.5 rounded"
                        style={{
                          background: row.type === 'OR' ? 'var(--primary-50)' : '#F5F3FF',
                          color:      row.type === 'OR' ? 'var(--primary-solid)' : '#7C3AED',
                        }}>
                        {row.type}
                      </span>
                      <ExternalLink size={11} style={{ color: 'var(--text-disabled)' }} />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{row.ref}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{row.client}</td>
                  <td className="px-3 py-2.5 text-xs font-medium" style={{ color: 'var(--text)' }}>{row.materiel}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-muted)' }}>{row.serie}</td>
                  <td className="px-3 py-2.5 text-xs truncate" style={{ color: 'var(--text)', maxWidth: 0 }} title={row.titre}>{row.titre}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--primary-solid)' }}>{fmtH(row.affecte)}</span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {row.pointe != null
                      ? <span className="text-sm font-bold tabular-nums" style={{ color: '#15803D' }}>{fmtH(row.pointe)}</span>
                      : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-subtle)' }}>
                <td colSpan={6} className="px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-right"
                  style={{ color: 'var(--text-muted)' }}>Total</td>
                <td className="px-4 py-2.5 text-center">
                  <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--primary-solid)' }}>{fmtH(totalAff)}</span>
                </td>
                <td className="px-4 py-2.5 text-center">
                  {totalPt > 0
                    ? <span className="text-sm font-bold tabular-nums" style={{ color: '#15803D' }}>{fmtH(totalPt)}</span>
                    : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {all.length === 0 && !formMode && (
        <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
          Aucune affectation pour ce jour
        </div>
      )}

      {formMode === 'or' && (
        <div className="px-5 py-4 space-y-3" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
            Affecter un ordre de réparation
          </p>
          <div className="flex gap-3 flex-wrap items-end">
            {/* Recherche OR */}
            <div style={{ flex: '3 1 220px', position: 'relative' }}>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                Ordre de réparation <span style={{ color: '#DC2626' }}>*</span>
              </label>
              {orSelected ? (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg border text-sm"
                  style={{ borderColor: 'var(--primary-solid)', background: 'var(--primary-50)' }}>
                  <span className="font-semibold" style={{ color: 'var(--primary-solid)' }}>
                    {orSelected.numero} — {orSelected.client_nom || orSelected.materiel_nom || ''}
                  </span>
                  <button onClick={() => { setOrSelected(null); setOrSearch(''); }}
                    className="ml-2" style={{ color: 'var(--text-muted)' }}>
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <>
                  <input type="text" value={orSearch} onChange={e => setOrSearch(e.target.value)}
                    placeholder="Numéro OR, client, matériel…"
                    className={inp} style={inpSt} />
                  {orLoading && (
                    <div className="absolute right-3 top-8">
                      <div className="animate-spin w-3.5 h-3.5 border-2 border-t-transparent rounded-full"
                        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                    </div>
                  )}
                  {orOptions.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 rounded-lg border shadow-lg overflow-hidden"
                      style={{ borderColor: 'var(--border)', background: 'white', top: '100%' }}>
                      {orOptions.map(o => (
                        <button key={o.ordre_id} type="button"
                          onClick={() => { setOrSelected(o); setOrSearch(''); setOrOptions([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b last:border-0"
                          style={{ borderColor: 'var(--border-light)' }}>
                          <span className="font-semibold" style={{ color: 'var(--primary-solid)' }}>{o.numero}</span>
                          <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                            {o.client_nom} · {o.materiel_nom || ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Durée */}
            <div style={{ flex: '1 1 90px' }}>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                Durée (h) <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input type="number" value={orDuree} onChange={e => setOrDuree(e.target.value)}
                placeholder="2.5" min="0.25" max="24" step="0.25"
                className={inp} style={inpSt} />
            </div>
            {/* Note */}
            <div style={{ flex: '2 1 160px' }}>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Note</label>
              <input type="text" value={orNote} onChange={e => setOrNote(e.target.value)}
                placeholder="Optionnel…" maxLength={300}
                className={inp} style={inpSt} />
            </div>
            {/* Boutons */}
            <div className="flex gap-2">
              <button onClick={handleAddOR} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'var(--primary-gradient)', flexShrink: 0 }}>
                {saving ? <div className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full" /> : <Plus size={14} />}
                Affecter
              </button>
              <button onClick={() => toggleForm(null)}
                className="px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Formulaire TNF ── */}
      {formMode === 'tnf' && (
        <div className="px-5 py-4" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
            Ajouter un temps non facturable
          </p>
          <div className="flex gap-3 flex-wrap items-end">
            {/* Type TNF */}
            <div style={{ flex: '2 1 180px' }}>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                Type TNF <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <select value={tnfTypeId} onChange={e => setTnfTypeId(e.target.value)}
                className={inp} style={inpSt}>
                <option value="">— Sélectionner —</option>
                {typesTNF.map(t => (
                  <option key={t.type_tnf_id} value={t.type_tnf_id}>{t.libelle}</option>
                ))}
              </select>
            </div>
            {/* Durée */}
            <div style={{ flex: '1 1 90px' }}>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                Durée (h) <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input type="number" value={tnfDuree} onChange={e => setTnfDuree(e.target.value)}
                placeholder="1.5" min="0.25" max="24" step="0.25"
                className={inp} style={inpSt} />
            </div>
            {/* Date début */}
            <div style={{ flex: '1 1 130px' }}>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                Date début <span style={{ color: '#DC2626' }}>*</span>
              </label>
              <input type="date" value={tnfDateDebut}
                onChange={e => { setTnfDateDebut(e.target.value); if (e.target.value > tnfDateFin) setTnfDateFin(e.target.value); }}
                className={inp} style={inpSt} />
            </div>
            {/* Date fin */}
            <div style={{ flex: '1 1 130px' }}>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                Date fin
              </label>
              <input type="date" value={tnfDateFin} min={tnfDateDebut}
                onChange={e => setTnfDateFin(e.target.value)}
                className={inp} style={inpSt} />
            </div>
            {/* Note */}
            <div style={{ flex: '2 1 160px' }}>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Note</label>
              <input type="text" value={tnfNote} onChange={e => setTnfNote(e.target.value)}
                placeholder="Optionnel…" maxLength={300}
                className={inp} style={inpSt} />
            </div>
            {/* Boutons */}
            <div className="flex gap-2">
              <button onClick={handleAddTNF} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'var(--primary-gradient)', flexShrink: 0 }}>
                {saving ? <div className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full" /> : <Plus size={14} />}
                Ajouter
              </button>
              <button onClick={() => toggleForm(null)}
                className="px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Cellule cliquable ────────────────────────────────────────────────────────

function Cell({ value, style, onClick, colSelected }) {
  return (
    <td
      onClick={onClick}
      className={onClick ? 'cursor-pointer' : ''}
      style={{
        padding: '5px 10px',
        textAlign: 'center',
        verticalAlign: 'middle',
        borderLeft: '1px solid var(--border-light)',
        background: colSelected ? '#EFF6FF' : (style?.background || 'transparent'),
        transition: 'background 0.15s',
        ...style,
        ...(colSelected ? { background: '#EFF6FF' } : {}),
      }}
    >
      {value}
    </td>
  );
}

// ─── Bloc 4 lignes par technicien ─────────────────────────────────────────────

function BlocTech({ tech, jours7, selected, onSelect }) {
  const base = tech.heures_base || 0;

  // Totaux semaine
  const totContrat = base;
  const totAff = tech.total_affectees || 0;
  const totPt  = tech.total_pointees  || 0;
  const { label: ecartTotLabel, color: ecartTotColor, weight: ecartTotWeight } = ecartInfo(totContrat, totPt);

  const rowSep = { borderTop: '1px solid var(--border-light)' };

  return (
    <>
      {/* Séparateur entre techniciens */}
      <tr style={{ borderTop: '2px solid var(--border)' }}>
        {/* Nom technicien — span 4 lignes */}
        <td rowSpan={4} className="px-4" style={{ verticalAlign: 'middle', minWidth: 190 }}>
          <div className="flex items-center gap-2.5 py-1">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
              style={{ background: 'var(--primary-gradient)' }}>
              {(tech.nom || '?')[0].toUpperCase()}
            </div>
            <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{tech.nom}</span>
          </div>
        </td>

        {/* Étiquette ligne Contrat */}
        <td className="px-3 py-1.5 text-xs whitespace-nowrap" style={{ color: 'var(--text-placeholder)', minWidth: 110, fontStyle: 'italic' }}>
          Contrat
        </td>

        {/* Cellules Contrat par jour */}
        {jours7.map(iso => {
          const j = tech.jours[iso] || {};
          const quota = j.quota || 0;
          const colSel = selected?.iso === iso;
          return (
            <Cell key={iso}
              onClick={() => onSelect(tech, iso)}
              colSelected={colSel}
              value={
                quota > 0
                  ? <span className="text-xs tabular-nums" style={{ color: 'var(--text-placeholder)' }}>
                      {fmtH(quota)}
                    </span>
                  : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>
              }
            />
          );
        })}

        {/* Total Contrat */}
        <Cell value={
          base > 0
            ? <span className="text-xs tabular-nums" style={{ color: 'var(--text-placeholder)' }}>
                {fmtH(base)}
              </span>
            : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>
        } style={{ background: 'var(--bg-subtle)', borderLeft: '2px solid var(--border)' }} />
      </tr>

      {/* ── Ligne Affectées ── */}
      <tr style={rowSep}>
        <td className="px-3 py-1.5 text-xs font-medium whitespace-nowrap" style={{ color: 'var(--text-muted)', minWidth: 110 }}>
          Affectées
        </td>

        {jours7.map(iso => {
          const j = tech.jours[iso] || {};
          const aff   = j.affectees || 0;
          const quota = j.quota     || 0;
          const colSel  = selected?.iso === iso;
          // Violet si Affectées < Contrat (et contrat > 0)
          const sousCharge = quota > 0 && aff < quota;
          return (
            <Cell key={iso}
              onClick={() => onSelect(tech, iso)}
              colSelected={colSel}
              value={
                aff > 0
                  ? <span className="text-xs font-semibold tabular-nums" style={{ color: sousCharge ? '#7C3AED' : 'var(--primary-solid)' }}>
                      {fmtH(aff)}
                    </span>
                  : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>
              }
              style={{ background: colSel ? undefined : (sousCharge ? '#F5F3FF' : (aff > 0 ? 'var(--primary-50)' : 'transparent')) }}
            />
          );
        })}

        {/* Total Affectées */}
        <Cell value={
          totAff > 0
            ? <span className="text-xs font-semibold tabular-nums"
                style={{ color: (totContrat > 0 && totAff < totContrat) ? '#7C3AED' : 'var(--primary-solid)' }}>
                {fmtH(totAff)}
              </span>
            : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>
        } style={{ background: 'var(--bg-subtle)', borderLeft: '2px solid var(--border)' }} />
      </tr>

      {/* ── Ligne Pointées ── */}
      <tr style={rowSep}>
        <td className="px-3 py-1.5 text-xs font-medium whitespace-nowrap" style={{ color: 'var(--text-muted)', minWidth: 110 }}>
          Pointées
        </td>

        {jours7.map(iso => {
          const j   = tech.jours[iso] || {};
          const pt  = j.pointees || 0;
          const colSel = selected?.iso === iso;
          return (
            <Cell key={iso}
              onClick={() => onSelect(tech, iso)}
              colSelected={colSel}
              value={
                pt > 0
                  ? <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--text)' }}>
                      {fmtH(pt)}
                    </span>
                  : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>
              }
              style={{ background: 'transparent' }}
            />
          );
        })}

        {/* Total Pointées */}
        <Cell value={
          totPt > 0
            ? <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--text)' }}>
                {fmtH(totPt)}
              </span>
            : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>
        } style={{ background: 'var(--bg-subtle)', borderLeft: '2px solid var(--border)' }} />
      </tr>

      {/* ── Ligne Écart (Pointées − Contrat) ── */}
      <tr style={rowSep}>
        <td className="px-3 py-1.5 text-xs" style={{ color: 'var(--text-placeholder)', fontStyle: 'italic', minWidth: 110 }}>
          Écart
        </td>

        {jours7.map(iso => {
          const j   = tech.jours[iso] || {};
          const { label, color, weight } = ecartInfo(j.quota || 0, j.pointees || 0);
          const colSel = selected?.iso === iso;
          return (
            <Cell key={iso}
              onClick={() => onSelect(tech, iso)}
              colSelected={colSel}
              value={
                <span style={{ fontSize: '0.75rem', fontWeight: weight, color }}>
                  {label}
                </span>
              }
              style={{ background: 'transparent' }}
            />
          );
        })}

        {/* Écart total semaine */}
        <Cell value={
          <span style={{ fontSize: '0.75rem', fontWeight: ecartTotWeight, color: ecartTotColor }}>
            {ecartTotLabel}
          </span>
        } style={{ background: 'var(--bg-subtle)', borderLeft: '2px solid var(--border)' }} />
      </tr>
    </>
  );
}

// ─── Ligne totaux équipe (4 lignes : Contrat / Affectées / Pointées / Écart) ──

function LigneTotaux({ techniciens, jours7 }) {
  const rowSep = { borderTop: '1px solid var(--border-light)' };

  // Totaux hebdo
  const totContratSem = techniciens.reduce((s, t) => s + (t.heures_base || 0), 0);
  const totAffSem     = techniciens.reduce((s, t) => s + (t.total_affectees || 0), 0);
  const totPtSem      = techniciens.reduce((s, t) => s + (t.total_pointees  || 0), 0);
  const { label: ecartTotLabel, color: ecartTotColor, weight: ecartTotWeight } = ecartInfo(totContratSem, totPtSem);

  const labelCell = (txt) => (
    <td className="px-3 py-1.5 text-xs whitespace-nowrap" style={{ color: 'var(--text-placeholder)', minWidth: 110, fontStyle: 'italic' }}>
      {txt}
    </td>
  );

  return (
    <>
      {/* ── Ligne Contrat ── */}
      <tr style={{ borderTop: '3px solid var(--border)', background: 'var(--bg-subtle)' }}>
        <td rowSpan={4} className="px-4" style={{ verticalAlign: 'middle', minWidth: 190 }}>
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            Total équipe
          </span>
          <span className="ml-2 text-xs" style={{ color: 'var(--text-placeholder)' }}>
            ({techniciens.length} tech.)
          </span>
        </td>
        {labelCell('Contrat')}
        {jours7.map(iso => {
          const tot = techniciens.reduce((s, t) => s + ((t.jours[iso] || {}).quota || 0), 0);
          return (
            <td key={iso} className="py-1.5 text-center" style={{ borderLeft: '1px solid var(--border-light)' }}>
              {tot > 0
                ? <span className="text-xs tabular-nums" style={{ color: 'var(--text-placeholder)' }}>{fmtH(tot)}</span>
                : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>}
            </td>
          );
        })}
        <td className="py-1.5 text-center" style={{ borderLeft: '2px solid var(--border)' }}>
          {totContratSem > 0
            ? <span className="text-xs tabular-nums" style={{ color: 'var(--text-placeholder)' }}>{fmtH(totContratSem)}</span>
            : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>}
        </td>
      </tr>

      {/* ── Ligne Affectées ── */}
      <tr style={{ ...rowSep, background: 'var(--bg-subtle)' }}>
        {labelCell('Affectées')}
        {jours7.map(iso => {
          const totAff    = techniciens.reduce((s, t) => s + ((t.jours[iso] || {}).affectees || 0), 0);
          const totQuota  = techniciens.reduce((s, t) => s + ((t.jours[iso] || {}).quota     || 0), 0);
          const sousCharge = totQuota > 0 && totAff < totQuota;
          return (
            <td key={iso} className="py-1.5 text-center"
              style={{ borderLeft: '1px solid var(--border-light)', background: sousCharge ? '#F5F3FF' : (totAff > 0 ? 'var(--primary-50)' : 'transparent') }}>
              {totAff > 0
                ? <span className="text-xs font-semibold tabular-nums" style={{ color: sousCharge ? '#7C3AED' : 'var(--primary-solid)' }}>{fmtH(totAff)}</span>
                : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>}
            </td>
          );
        })}
        <td className="py-1.5 text-center" style={{ borderLeft: '2px solid var(--border)' }}>
          {totAffSem > 0
            ? <span className="text-xs font-semibold tabular-nums"
                style={{ color: (totContratSem > 0 && totAffSem < totContratSem) ? '#7C3AED' : 'var(--primary-solid)' }}>
                {fmtH(totAffSem)}
              </span>
            : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>}
        </td>
      </tr>

      {/* ── Ligne Pointées ── */}
      <tr style={{ ...rowSep, background: 'var(--bg-subtle)' }}>
        {labelCell('Pointées')}
        {jours7.map(iso => {
          const tot = techniciens.reduce((s, t) => s + ((t.jours[iso] || {}).pointees || 0), 0);
          return (
            <td key={iso} className="py-1.5 text-center" style={{ borderLeft: '1px solid var(--border-light)' }}>
              {tot > 0
                ? <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--text)' }}>{fmtH(tot)}</span>
                : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>}
            </td>
          );
        })}
        <td className="py-1.5 text-center" style={{ borderLeft: '2px solid var(--border)' }}>
          {totPtSem > 0
            ? <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--text)' }}>{fmtH(totPtSem)}</span>
            : <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>—</span>}
        </td>
      </tr>

      {/* ── Ligne Écart ── */}
      <tr style={{ ...rowSep, background: 'var(--bg-subtle)' }}>
        {labelCell('Écart')}
        {jours7.map(iso => {
          const totQuota = techniciens.reduce((s, t) => s + ((t.jours[iso] || {}).quota    || 0), 0);
          const totPt    = techniciens.reduce((s, t) => s + ((t.jours[iso] || {}).pointees || 0), 0);
          const { label, color, weight } = ecartInfo(totQuota, totPt);
          return (
            <td key={iso} className="py-1.5 text-center" style={{ borderLeft: '1px solid var(--border-light)' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: weight, color }}>{label}</span>
            </td>
          );
        })}
        <td className="py-1.5 text-center" style={{ borderLeft: '2px solid var(--border)' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: ecartTotWeight, color: ecartTotColor }}>{ecartTotLabel}</span>
        </td>
      </tr>
    </>
  );
}

// ─── Page principale ─────────────────────────────────────────────────────────

export default function TableauDeCharge() {
  const { activeAgences } = useSociete();
  const [semaine,    setSemaine]    = useState(getLundi(todayISO()));
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [selected,   setSelected]   = useState(null); // { tech, iso }

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(null);
    try {
      const r = await api.getTableauDeCharge(semaine);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [semaine, activeAgences]);

  useEffect(() => { load(); }, [load]);

  const prevWeek = () => setSemaine(s => addDays(s, -7));
  const nextWeek = () => setSemaine(s => addDays(s, 7));
  const goToday  = () => setSemaine(getLundi(todayISO()));

  // 7 jours complets (Lun–Dim)
  const jours7      = data?.jours || [];
  const techniciens = data?.techniciens || [];

  const handleSelect = (tech, iso) => {
    setSelected(prev =>
      prev?.employe_id === tech.employe_id && prev?.iso === iso
        ? null  // toggle off
        : { ...tech, employe_id: tech.employe_id, iso }
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
            Tableau de charge
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Heures affectées et pointées par technicien
          </p>
        </div>
        <button onClick={load} data-testid="refresh-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* Navigation semaine */}
      <div className="flex items-center gap-2">
        <button onClick={prevWeek} data-testid="prev-week"
          className="p-2 rounded-lg border hover:bg-gray-50 transition-colors"
          style={{ borderColor: 'var(--border)' }}>
          <ChevronLeft size={16} style={{ color: 'var(--text-muted)' }} />
        </button>
        <button onClick={goToday} data-testid="today-btn"
          className="px-3 py-1.5 rounded-lg border text-xs font-semibold hover:bg-gray-50 transition-colors"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          Aujourd'hui
        </button>
        <button onClick={nextWeek} data-testid="next-week"
          className="p-2 rounded-lg border hover:bg-gray-50 transition-colors"
          style={{ borderColor: 'var(--border)' }}>
          <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
        </button>
        <span className="ml-1 text-sm font-semibold" style={{ color: 'var(--text)' }}>
          Semaine du {fmtWeek(semaine)}
        </span>
      </div>

      {/* Légende compacte */}
      <div className="flex items-center gap-5 text-xs flex-wrap" style={{ color: 'var(--text-muted)' }}>
        <span style={{ color: 'var(--text-placeholder)', fontStyle: 'italic' }}>Contrat</span>
        <span style={{ color: '#7C3AED', fontWeight: 600 }}>Affectées &lt; Contrat</span>
        <span style={{ color: 'var(--primary-solid)', fontWeight: 600 }}>Affectées</span>
        <span style={{ color: 'var(--text)' }}>Pointées</span>
        <span style={{ color: '#15803D' }}>Écart = équilibré</span>
        <span style={{ color: '#1D4ED8' }}>Écart +Xh dépassement</span>
        <span style={{ color: '#DC2626' }}>Écart −Xh sous-réalisation</span>
        <span style={{ color: 'var(--text-placeholder)', fontStyle: 'italic' }}>Cliquer sur une cellule pour le détail</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full"
            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : techniciens.length === 0 ? (
        <div className="rounded-xl py-16 text-center" style={{ border: '1px dashed var(--border)' }}>
          <Clock size={32} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
            Aucun technicien actif
          </p>
          <p className="text-xs" style={{ color: 'var(--text-placeholder)' }}>
            Créez des employés avec la méthode "Pointage atelier (OR + TNF)" dans Employés
          </p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm" style={{ tableLayout: 'auto' }}>
              <thead>
                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '2px solid var(--border)' }}>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)', minWidth: 190 }}>
                    Technicien
                  </th>
                  <th className="px-3 py-2.5" style={{ minWidth: 110 }} />
                  {jours7.map((iso, i) => {
                    const isWeekend = i >= 5;
                    return (
                      <th key={iso} className="py-2.5 text-center text-xs font-semibold uppercase tracking-wide"
                        style={{
                          color: selected?.iso === iso ? 'var(--primary-solid)' : (isWeekend ? 'var(--text-placeholder)' : 'var(--text-muted)'),
                          minWidth: 85,
                          borderLeft: '1px solid var(--border-light)',
                          background: selected?.iso === iso ? '#DBEAFE' : (isWeekend ? 'var(--bg-subtle)' : 'transparent'),
                          transition: 'background 0.15s',
                        }}>
                        <div>{JOURS_COURTS[i]}</div>
                        <div className="font-normal" style={{ color: selected?.iso === iso ? 'var(--primary-solid)' : 'var(--text-placeholder)', fontSize: '0.7rem' }}>
                          {new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                        </div>
                      </th>
                    );
                  })}
                  <th className="px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)', borderLeft: '2px solid var(--border)', minWidth: 80 }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {techniciens.map(tech => (
                  <BlocTech
                    key={tech.employe_id}
                    tech={tech}
                    jours7={jours7}
                    selected={selected}
                    onSelect={handleSelect}
                  />
                ))}
              </tbody>
              <tfoot>
                <LigneTotaux techniciens={techniciens} jours7={jours7} />
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Panneau de détail journalier */}
      {selected && (
        <DetailPanel
          tech={techniciens.find(t => t.employe_id === selected.employe_id)}
          iso={selected.iso}
          onClose={() => setSelected(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
}
